import { Component, ViewEncapsulation } from '@angular/core';
import { ButtonTriggerEvent } from '../../../common/components';
import { HubPlaceAdditionalData } from '../../../models/hub/hub.google.place.data';
import { HubPlace } from '../../../models/hub/hub.place';
import { HubVisitorToggleDataSource } from '../../../models/hub/hub.visitor.feature.toggle';
import { HubApiService } from '../../../services/hub.api.service';
import { ImageService } from '../../../services/image.service';
import { BaseComponent } from '../../../common/components/base.component';
import { MatDialogRef } from '@angular/material';
import { AlertifyService } from '../../../services/alertify.service';
import { PlaceService } from '../../../services/place.service';
import { ToggleAndDataSourceStore } from '../../../stores/hub.calendar.store';
import { ConfirmModalActions, ConfirmModalData } from '../../../common/components/confirm.modal/confirm.modal.provider';
import { ModalService } from '../../../common/services/modal.service';
import { ConfirmModalComponent } from '../../../common/components/confirm.modal/confirm.modal.component';
import { clone, isEmptyArray } from '../../../common/extensions';
import { config } from '../../../../config/config';
import { InputModalComponent } from "../../../common/components/input.modal/input.modal.component";
import { InputModalActions, InputModalData } from "../../../common/components/input.modal/input.modal.provider";
import { EditManuallyPlaceImagesModalActions } from './edit.manually.place.images.modal.provider';

@Component({
  templateUrl: './edit.manually.place.images.modal.component.html',
  styleUrls: ['./edit.manually.place.images.modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class EditManuallyPlaceImagesModalComponent extends BaseComponent {
  public place: HubPlace;
  public hubName: string;
  public placeAdditionalData: HubPlaceAdditionalData;
  public isPlaceInfoLoaded = false;
  public isDragging = false;
  public dragFrom = '';
  public dropTargets = [];

  public coverImage: string = '';
  public publishedImages: string[] = [];
  public compensateForPublished: any[] = [];
  public compensateForAvailable: any[] = [];

  public ownedImages: {
    url: string;
    status: 'initial' | 'uploading' | 'uploaded' | 'failed' | 'done';
  }[] = [];
  public compensateForOwned: any[] = [];

  public googleKey = '';
  public totalImageCount = 10;
  public totalImageCountThatSysAdminSet = 10;
  public useFirstImgAsCover = true;
  public selectedImageFiles: File[];

  constructor(
    private dialogRef: MatDialogRef<EditManuallyPlaceImagesModalComponent>,
    private actions: EditManuallyPlaceImagesModalActions,
    private hubApiService: HubApiService,
    public imageService: ImageService,
    private alertify: AlertifyService,
    private modalService: ModalService,
    private toggleAndDataSourceStore: ToggleAndDataSourceStore,
    data: HubPlace
  ) {
    super();
    this.place = Object.assign({}, data);
    this.hubName = this.place.hubName;

    this.toggleAndDataSourceStore.toggleAndDataSourceCommand
      .takeUntil(this.isDestroyed)
      .subscribe((command: HubVisitorToggleDataSource) => {
        if (command && command.dataSource.placeImagesShownCount) {
          this.totalImageCountThatSysAdminSet = command.dataSource.placeImagesShownCount;
        }
        this.getPlaceAdditionalData();
      });
  }

  public async getPlaceAdditionalData() {
    this.buildImages();
    this.compensate();
    this.isPlaceInfoLoaded = true;
  }

  public async confirm(event: ButtonTriggerEvent) {
    if (!this.publishedImages.length) {
      event.immediateComplete();
      this.alertify.warning('Please select at least one allowed image');
      return;
    }

    let actions = <ConfirmModalActions>{
      confirm: async (e: ButtonTriggerEvent) => {
        e.complete();

        if (!this.place.cover) this.place.cover = { source: '', isDefault: false };
        this.coverImage = this.publishedImages[0];
        
        this.place.allowedGooglePhotos = this.publishedImages;
        await this.uploadOwnedImagesToAWS();

        if (this.ownedImages && this.ownedImages.any((img) => img.status === 'failed')) {
          event.immediateComplete();
          return;
        }
        this.place.ownedPhotos = this.ownedImages.map((x) => x.url);
        this.ownedImages.any((x) => x.status === 'uploaded') && (await this.myWait(1000));
        event.immediateComplete();

        this.actions.confirm(e, this.place, this.coverImage);
        this.dialogRef.close();
      },
      cancel: () => {
        event.immediateComplete();
      }
    };
    let data = <ConfirmModalData>{
      confirmModalIcon: 'warning',
      confirmModalTitle: 'Alert',
      confirmModalDescription:
        'Please note that any images uploaded or used in ' +
        'this software must either be royalty-free or owned by the client. By uploading or using an image, you ' +
        'acknowledge that you have the necessary rights to use it and that you will be solely responsible for any ' +
        'copyright infringement or other legal issues that may arise from the use of the image. We strongly advise our ' +
        'users to exercise due diligence and ensure that they have the necessary rights to use any images uploaded or ' +
        'used in this software.',
      confirmModalConfirmButtonText: 'Yes',
      confirmModalCancelButtonText: 'Cancel',
      isAsync: true,
    };

    this.modalService.show({
      component: ConfirmModalComponent,
      providers: [
        { provide: ConfirmModalActions, useValue: actions },
        { provide: ConfirmModalData, useValue: data },
      ],
    });
  }

  public async uploadOwnedImagesToAWS() {
    for (const img of this.ownedImages) {
      if (img.url.includes('amazonaws')) continue;

      img.status = 'uploading';
      const res = await this.hubApiService.uploadBase64ImageToAWS(
        img.url,
        config.compressImgWidth,
        'places/iti-user-upload/'
      );
      if (res.success) {
        img.url = res.url;
        img.status = 'uploaded';
        setTimeout(() => {
          img.status = 'done';
        }, 3000);
      } else {
        img.status = 'failed';
      }
    }
  }

  public removeOwnImage(index: number) {
    this.ownedImages.splice(index, 1);
  }

  public getImage(imgUrl: string, isRealReference = true) {
    if (imgUrl.startsWith('http') || imgUrl.startsWith('data:')) {
      return imgUrl;
    }

    let url = PlaceService.getGooglePlaceImageFullUrl(imgUrl, this.googleKey, isRealReference);
    return this.imageService.imageSourceUrl(url);
  }

  public dragEnter($event) {
    if ($event && $event.currentTarget) {
      this.restoreItemStyle();
      $event.currentTarget.style.border = '2px dashed green';
      if (!this.dropTargets.any((x) => x === $event.currentTarget)) {
        this.dropTargets.push($event.currentTarget);
      }
    }
  }

  public async startDrag($event, from: string) {
    if (this.isWaitingForUpload($event.srcElement.currentSrc)) return;

    if (!this.isDragging) {
      this.isDragging = true;
      this.dragFrom = from;
    }
  }

  public async onItemDrop(e: any, to: string, index: number = -1) {
    this.restoreItemStyle();

    if (this.dragFrom === 'owned') {
      if (this.ownedImages.any((x) => this.isWaitingForUpload(x.url))) {
        this.alertify.warning('Please wait for uploading, and move the image again.');
        await this.uploadOwnedImagesToAWS();
        this.isDragging = false;
        return;
      }
    }

    if (e.dragData) {
      let photoReference = PlaceService.getGooglePlaceReference(e.dragData);
      if (this.dragFrom === 'allowed' && to === 'allowed') {
        
        if(index === -1) {
          this.isDragging = false;
          return;
        }

        let dragDataIndex = this.publishedImages.indexOf(photoReference);
        let temp = this.publishedImages[index];
        this.publishedImages[index] = photoReference;
        this.publishedImages[dragDataIndex] = temp;
      }

      if (this.dragFrom === 'owned' && to === 'allowed') {
        if (!this.publishedImages.any((x) => x === photoReference)) {          
          if (index >= 0 && index <= this.publishedImages.length) {
            let temp = this.publishedImages[index];
            this.publishedImages[index] = photoReference;
            !this.isWaitingForUpload(temp);
          } else {           
            this.publishedImages.push(photoReference);
          }
        } else {
          this.alertify.warning('This image is already added');
        }
      }

      this.compensate();
    }
    this.isDragging = false;
  }

  public removeOwnedImageFromAllowed(image: string) {
    this.publishedImages = this.publishedImages.filter((x) => x !== image);
    this.compensate();
  }

  public isWaitingForUpload(image: string) {
    if (!image) return false;
    return !image.includes('amazonaws');
  }

  public close(): void {
    if (this.actions.cancel) {
      this.actions.cancel();
    }
    this.dialogRef.close();
  }
  public enterImageLink() {
    let actions = <InputModalActions>{
      confirm: async (event: ButtonTriggerEvent, url) => {
        console.log(url);
        this.ownedImages.push({
          url,
          status: 'initial',
        });
        await this.uploadOwnedImagesToAWS();
      },
    };

    this.modalService.show({
      component: InputModalComponent,
      providers: [
        { provide: InputModalActions, useValue: actions },
        {
          provide: InputModalData,
          useValue: new InputModalData('Enter image link', '', null, 'OK', 'CANCEL', false),
        },
      ],
      panelClass: 'width-modal-2',
    });
  }

  public async onImageChange($event) {
    if ($event.srcElement.files.length > 10 || $event.srcElement.files.length + this.ownedImages.length > 10) {
      this.alertify.warning(`You can't upload more than 10 images`);
    }
    this.selectedImageFiles = $event.srcElement.files;

    if (this.selectedImageFiles && this.selectedImageFiles.length > 0) {
      for (let i = 0; i < this.selectedImageFiles.length; i++) {
        if (this.ownedImages.length >= 10) break;

        const selectedImageFile = await this.imageService.compressImageFileToBase64(this.selectedImageFiles[i]);
        this.ownedImages.push({
          url: selectedImageFile,
          status: 'initial',
        });
      }

      await this.uploadOwnedImagesToAWS();
    }
    $event.srcElement.value = '';
  }

  private restoreItemStyle() {
    this.dropTargets.forEach((x) => {
      x.style.border = 'none';
    });
  }

  private buildImages() {
    this.publishedImages = [];
    this.ownedImages = [];

    let allowedPhotos = clone(this.place.allowedGooglePhotos) || [];
    if (this.place.cover && this.place.cover.source) {
      const sourceStr = this.place.cover.source;
      if (sourceStr.includes(';')) {
        sourceStr.split(';').filter(u => u.trim()).forEach((url, i) => {
          allowedPhotos[i] = allowedPhotos[i] || url;
        });
      } else {
        allowedPhotos[0] = sourceStr;
      }
    }
    if (this.place.allowedGooglePhotos && this.place.allowedGooglePhotos.length >= 0) {
      let images = [...this.publishedImages, ...allowedPhotos];
      this.publishedImages = images.slice(0, this.totalImageCountThatSysAdminSet);
    }
    if (this.place.ownedPhotos && this.place.ownedPhotos.length >= 0) {
      this.ownedImages = this.place.ownedPhotos.map((x) => {
        return { url: x, status: 'initial' as any };
      });
    }
  }

  private compensate() {
    if (this.publishedImages && this.publishedImages.length >= 0) {
      this.compensateForPublished = [];
      for (let i = 0; i < this.totalImageCountThatSysAdminSet - this.publishedImages.length; i++) {
        this.compensateForPublished.push({});
      }
    }
  }

  private myWait(milSeconds) {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        resolve(null);
      }, milSeconds);
    });
  }
}



// WEBPACK FOOTER //
// D:/Work/Development/ImGoing/IMGoing/web/src/components/hub/edit.manually.place.images.modal/edit.manually.place.images.modal.component.ts